<script setup>
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import Modal from "@/components/Modal";
import SelectInput from "@/components/Select/SelectInput";
import SessionsList from "./SessionsList.vue";
import DayPicker from "@/modules/management-panel/components/DayPicker/DayPicker.vue";
import { getWeekdaysForTerm } from "@/components/methods";

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false,
    },
    eventName: {
        type: String,
        default: "",
    },
    headerText: {
        type: String,
        default: "",
    },
    venues: {
        type: Array,
        default: () => [],
    },
    sessions: {
        type: Array,
        default: () => [],
    },
    terms: {
        type: Array,
        default: () => [],
    },
});

const route = useRoute();

const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const selectedVenue = ref({
    title: "All venues",
    venueId: null,
});

const selectedDay = ref("mon");

const emit = defineEmits(["close"]);

const getVenues = computed(() => {
    const venuesList = [
        {
            title: "All venues",
            venueId: null,
        },
    ];

    // eslint-disable-next-line array-callback-return
    props.venues?.map((venue) => {
        venuesList.push({
            title: `${venue.name} , ${venue.postcode}`,
            venueId: venue.venue_id,
        });
    });

    return venuesList;
});

function updateDay(day) {
    selectedDay.value = day;
}

function getWeekdayFromSession(session) {
    return session.day?.substring(0, 3) || undefined;
}

function getAllDaysForDailySession(session) {
    const term = props.terms.find((t) => t.termdate_id === session.termdate_id);
    return getWeekdaysForTerm(term.start, term.end);
}

function getAllDaysForDailySessions(sessions) {
    const res = sessions.map((session) => getAllDaysForDailySession(session));

    return [...new Set([...res.flat()])];
}

const daysUnused = computed(() => {
    // eslint-disable-next-line array-callback-return, consistent-return
    const sessionDays = [...new Set(props.sessions.map((session) => {
        if (selectedVenue.value.venueId === null) {
            return getWeekdayFromSession(session);
        }

        if (selectedVenue.value.venueId === session.venue_id) {
            return getWeekdayFromSession(session);
        }
    }).filter((el) => el !== undefined))];

    const sessionsForAllWeek = props.sessions.filter(
        (session) => {
            if (selectedVenue.value.venueId !== null) {
                return session.day === null && session.venue_id === selectedVenue.value.venueId;
            }

            return session.day === null;
        },
    );
    const daysForAllWeekSessions = getAllDaysForDailySessions(sessionsForAllWeek);

    const allSessionDays = [...new Set([...sessionDays, ...daysForAllWeekSessions])];

    const result = days.filter((day) => !allSessionDays.includes(day));

    return result;
});

function setSelectedDay() {
    const disabledDays = daysUnused.value;

    const availableDays = days.filter((day) => !disabledDays.includes(day));

    selectedDay.value = availableDays[0] ?? "mon";
}

function updateSelectedVenue(venue) {
    selectedVenue.value = venue;

    setSelectedDay();
}

const getSessions = computed(() => props.sessions.filter((session) => {
    if (selectedVenue.value.venueId === null) {
        return session.day?.substring(0, 3) === selectedDay.value || session.day === null;
    }

    return (
        session.day !== null
            ? session.day?.substring(0, 3) === selectedDay.value
            && session.venue_id === selectedVenue.value.venueId
            : session.venue_id === selectedVenue.value.venueId
            && getAllDaysForDailySession(session).includes(selectedDay.value)
    );
}));

watch(
    () => props.venues,
    () => {
        const venue = getVenues.value.find((v) => v.venueId === route.params.venueId);
        selectedVenue.value = venue;

        setSelectedDay();
    },
);

</script>
<template>
    <Modal
        :show-modal="props.showModal"
        :hide-footer="true"
        @close-modal="emit('close')"
    >
        <div class="header">{{ props.headerText }}</div>
        <h2 class="mt-2 mb-3">{{ props.eventName }}</h2>
        <div class="mt-3">
            <SelectInput
                :options="getVenues"
                label="title"
                :default="selectedVenue?.title"
                @change="updateSelectedVenue"
            />
        </div>

        <div class="sessions-list">
            <DayPicker
                :day-selected="selectedDay"
                @change-day="updateDay"
                :disabledDays="daysUnused"
                class="full-width"
            />
            <SessionsList
                :sessions="getSessions"
            />
        </div>
    </Modal>
</template>
<style scoped>

h2 {
    font-size: 1.5rem;
}

.header {
    font-family: 'Poppins';
    font-style: normal;
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 1.3rem;
    color: #999999;
}

.sessions-list {
    background-color: #efefef;
    margin-top: 1.6rem;
    padding: 1rem;
}
</style>
